import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Download, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { useProjectContext } from '../../context/ProjectContext';
import { useDraftDock } from '../../context/DraftDockContext';
import { isDraft, isDraftAttachment } from '../../components/files/fileMeta';
import DraftInlineSlot from '../../components/drafts/DraftInlineSlot';
import { useCreateFile, useCreateTask, useUpdateTask } from '../../hooks/useProjectQueries';
import { ProjectHomeSkeleton } from '../../components/skeletons/PageSkeletons';
import { XmlPreviewCompact, isXmlFile } from '../../components/XmlViewer';
import { dueBucket } from '../../components/tasks/taskDates';
import { isDemoted } from '../../components/tasks/taskMeta';
import { buildNewTask } from '../../components/tasks/buildTask';

import ProjectBar from '../../components/home/ProjectBar';
import HomeConsole from '../../components/home/HomeConsole';
import NowPane from '../../components/home/NowPane';
import AdvisorPane from '../../components/home/AdvisorPane';
import ArtifactsPane from '../../components/home/ArtifactsPane';
import ChatsPane from '../../components/home/ChatsPane';
import PulsePane from '../../components/home/PulsePane';

/**
 * ProjectHome — the project console.
 *
 * Was a single scrolling Surface holding a 600-line page of cards. It is now a
 * viewport-locked mosaic: an instrument bar over a tree of resizable panes,
 * each scrolling internally, laid out like every other route in the shell.
 *
 * This file is the composition root — data derivation, dialogs and shortcut
 * wiring. Geometry lives in HomeConsole, and each widget is its own memoised
 * module under components/home/, so dragging one seam cannot re-render the
 * contents of the panes beside it.
 */

// Enough history to fill a pane dragged to full height, and a cap so the feed
// can't grow without bound on a busy project.
const ACTIVITY_LIMIT = 24;

// Hoisted so `project.links ?? NO_LINKS` hands ProjectBar the same array every
// render. A fresh `[]` would defeat its memo on any project without links.
const NO_LINKS = [];

export default function ProjectHome() {
    const { projectId } = useParams();
    const navigate = useNavigate();

    const { project, tasks, files, dashboard, isLoading, readOnly, baseUrl } = useProjectContext();
    const draftDock = useDraftDock();

    // Destructured because React Query returns a fresh result object every
    // render while keeping `mutateAsync` itself stable. Depending on the object
    // would make every callback below a new function each render, and the
    // memoised panes would re-render on every keystroke in the composer.
    const { mutateAsync: updateTask } = useUpdateTask(projectId);
    const { mutateAsync: createTask } = useCreateTask(projectId);
    const { mutateAsync: createFile } = useCreateFile(projectId);

    const [isSending, setIsSending] = useState(false);
    const [taskDialogOpen, setTaskDialogOpen] = useState(false);
    const [docDialogOpen, setDocDialogOpen] = useState(false);
    const [newTaskTitle, setNewTaskTitle] = useState('');
    const [newDocName, setNewDocName] = useState('');
    const [previewFile, setPreviewFile] = useState(null);

    const recentChats = dashboard?.recent_chats ?? [];

    // One pass over each collection. The old version mapped files, mapped tasks,
    // concatenated, then fully sorted every item in the project on every single
    // mutation — to render ten rows.
    const { stats, pinnedFiles, activity, draftCount } = useMemo(() => {
        const now = new Date();
        let open = 0;
        let completed = 0;
        let dueSoon = 0;
        const feed = [];

        let activeTasks = 0;
        for (const task of tasks) {
            // Parked work is out of every number on this page, denominator
            // included. Left in, deferring twenty things you consciously chose
            // not to do would permanently depress the completion bar — the page
            // would punish you for using the backlog, which is the opposite of
            // what it is for. It is also kept out of the activity feed: parking
            // something is not progress worth reporting.
            if (isDemoted(task)) continue;
            activeTasks += 1;

            if (task.status === 'done') {
                completed += 1;
            } else {
                open += 1;
                const bucket = dueBucket(task.due_date, now);
                if (bucket === 'overdue' || bucket === 'today' || bucket === 'week') dueSoon += 1;
            }
            if (task.created_at) {
                feed.push({
                    key: `task-${task.id}`,
                    type: 'task',
                    label: task.title,
                    date: new Date(task.created_at),
                });
            }
        }

        const pinned = [];
        let drafts = 0;
        let artifacts = 0;
        for (const file of files) {
            // An image pasted into a draft is a dependency of that draft. It has
            // no business in the pinned list, the activity feed or the count.
            if (isDraftAttachment(file)) continue;

            if (file.pinned) pinned.push(file);

            if (isDraft(file)) {
                drafts += 1;
                // Nor does a scratchpad: autosave writes it every 800ms, so a
                // note you keep open all afternoon would push everything else
                // out of Pulse.
                continue;
            }

            artifacts += 1;
            if (file.last_edited) {
                feed.push({
                    key: `file-${file.id}`,
                    type: 'file',
                    label: file.name,
                    date: new Date(file.last_edited),
                    fileId: file.id,
                });
            }
        }

        feed.sort((a, b) => b.date - a.date);

        return {
            stats: {
                open,
                dueSoon,
                completedTasks: completed,
                taskCount: activeTasks,
                fileCount: artifacts,
                completionPercent: activeTasks > 0 ? Math.round((completed / activeTasks) * 100) : 0,
            },
            pinnedFiles: pinned,
            activity: feed.slice(0, ACTIVITY_LIMIT),
            draftCount: drafts,
        };
    }, [tasks, files]);

    // ── Actions ──────────────────────────────────────────────────────────────
    // All stable, so the memoised panes only re-render when their data changes.

    const goToBoard = useCallback(() => navigate(`${baseUrl}/tasks`), [navigate, baseUrl]);
    const goToFiles = useCallback(() => navigate(`${baseUrl}/files`), [navigate, baseUrl]);
    const goToChat = useCallback(() => navigate(`${baseUrl}/chat`), [navigate, baseUrl]);
    const openChat = useCallback((id) => navigate(`${baseUrl}/chat?session=${id}`), [navigate, baseUrl]);
    const manageLinks = useCallback(() => navigate(`${baseUrl}/settings?section=links`), [navigate, baseUrl]);

    const openActivity = useCallback((entry) => {
        if (entry.type === 'file') navigate(`${baseUrl}/editor/${entry.fileId}`);
        else navigate(`${baseUrl}/tasks`);
    }, [navigate, baseUrl]);

    // Goes through the mutation, not a bare api.put. The previous version called
    // an undefined `loadData()` here, so completing the focus task threw a
    // ReferenceError and invalidated no cache at all.
    const completeTask = useCallback(async (id) => {
        try {
            await updateTask({ id, updates: { status: 'done' } });
        } catch {
            /* the mutation hook surfaces the failure */
        }
    }, [updateTask]);

    const sendToAdvisor = useCallback(async (message) => {
        if (readOnly || isSending) return;
        setIsSending(true);
        try {
            const session = await api.post(`/projects/${projectId}/chat-sessions`);
            await api.post(`/chat-sessions/${session.data.id}/messages`, {
                message,
                context_mode: 'selective',
                referenced_files: [],
                web_search: false,
                model_preset: 'fast',
            });
            navigate(`${baseUrl}/chat?session=${session.data.id}`);
        } catch {
            toast.error('Failed to send message');
        } finally {
            setIsSending(false);
        }
    }, [readOnly, isSending, projectId, navigate, baseUrl]);

    // The scratchpad lives in the project Layout, above this route — the bar
    // button and the `q` shortcut are two doors into the same editor. Both mean
    // a blank one: the button reads "Draft", not "resume the last draft", and
    // the switcher inside is how you get back to those.
    const openDraft = useCallback(() => {
        if (readOnly) return;
        draftDock?.requestNew();
    }, [readOnly, draftDock]);

    const triggerUpload = useCallback(() => document.getElementById('quick-up')?.click(), []);
    const openTaskDialog = useCallback(() => setTaskDialogOpen(true), []);
    const openDocDialog = useCallback(() => setDocDialogOpen(true), []);
    const openPreview = useCallback((file) => setPreviewFile(file), []);

    const handleCreateTask = async (e) => {
        e.preventDefault();
        // Was hand-rolling its own defaults, which is why a task typed here came
        // out undated while the identical sentence typed on the board came out
        // scheduled.
        const task = buildNewTask(newTaskTitle);
        if (!task) return;
        try {
            await createTask(task);
            toast.success('Task created');
            setNewTaskTitle('');
            setTaskDialogOpen(false);
        } catch {
            toast.error('Failed to create task');
        }
    };

    const handleCreateDoc = async (e) => {
        e.preventDefault();
        const name = newDocName.endsWith('.md') ? newDocName : `${newDocName}.md`;
        try {
            await createFile({
                name, type: 'doc', category: 'Docs', content: '# New Document',
            });
            toast.success('Doc created');
            setNewDocName('');
            setDocDialogOpen(false);
        } catch {
            toast.error('Failed to create doc');
        }
    };

    const handleUpload = (e) => {
        const input = e.target;
        const file = input.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onerror = () => toast.error('Could not read that file');
        reader.onload = async (ev) => {
            const type = file.type.startsWith('image/') ? 'asset' : 'other';
            try {
                await createFile({
                    name: file.name,
                    type,
                    category: type === 'asset' ? 'Assets' : 'Docs',
                    content: ev.target.result,
                });
                toast.success('Uploaded');
            } catch {
                toast.error('Upload failed');
            }
        };

        if (file.type.startsWith('text/') || /\.(md|js|jsx|json)$/.test(file.name)) {
            reader.readAsText(file);
        } else {
            reader.readAsDataURL(file);
        }

        // Without this, picking the same file twice in a row fires no change
        // event and the upload silently does nothing.
        input.value = '';
    };

    const handleDownload = useCallback((file) => {
        if (!file?.content) {
            toast.error('Nothing to download');
            return;
        }
        let blob;
        try {
            if (file.content.startsWith('data:')) {
                const [header, payload = ''] = file.content.split(',');
                const mime = header.match(/:(.*?);/)?.[1] ?? 'application/octet-stream';
                const binary = atob(payload);
                const bytes = new Uint8Array(binary.length);
                for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
                blob = new Blob([bytes], { type: mime });
            } else {
                blob = new Blob([file.content], { type: 'text/plain' });
            }
        } catch {
            toast.error('Could not read this file');
            return;
        }

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = file.name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    }, []);

    // Tell the scratchpad this route has a pane for it before there is one to
    // hand over. It renders nothing at all until the console has painted rather
    // than starting life as a floating panel and hopping into the pane a beat
    // later — that hop re-mounts the editor and would drop the caret on every
    // cold load of Home.
    const declareInlineSlot = draftDock?.declareInlineSlot;
    useEffect(() => {
        if (readOnly || !declareInlineSlot) return undefined;
        declareInlineSlot(true);
        return () => declareInlineSlot(false);
    }, [readOnly, declareInlineSlot]);

    // Global shortcuts are dispatched as events by the project Layout. `readOnly`
    // is a real dependency: it was read here with an empty dep array, so the
    // listeners kept whatever value it had at mount.
    useEffect(() => {
        if (readOnly) return undefined;
        window.addEventListener('open-new-task', openTaskDialog);
        window.addEventListener('open-new-doc', openDocDialog);
        window.addEventListener('trigger-upload', triggerUpload);
        return () => {
            window.removeEventListener('open-new-task', openTaskDialog);
            window.removeEventListener('open-new-doc', openDocDialog);
            window.removeEventListener('trigger-upload', triggerUpload);
        };
    }, [readOnly, openTaskDialog, openDocDialog, triggerUpload]);

    if (isLoading || !project) return <ProjectHomeSkeleton />;

    const panes = {
        now: (
            <NowPane
                tasks={tasks}
                readOnly={readOnly}
                onComplete={completeTask}
                onOpenBoard={goToBoard}
                onNewTask={openTaskDialog}
            />
        ),
        // Both are hidden rather than shown empty on a share link: the chat
        // endpoints need an authenticated project id, which that route has none of.
        advisor: readOnly ? null : <AdvisorPane onSend={sendToAdvisor} isSending={isSending} />,
        artifacts: <ArtifactsPane files={pinnedFiles} onPreview={openPreview} onOpenFiles={goToFiles} />,
        chats: readOnly ? null : <ChatsPane chats={recentChats} onOpenChat={openChat} onOpenAll={goToChat} />,
        pulse: <PulsePane activity={activity} onOpen={openActivity} />,
        // An open scratchpad takes the signals column for as long as it's open,
        // rather than floating over the console. Collapsed doesn't count — that
        // is the state that exists precisely to give the dashboard back.
        draft: draftDock?.isOpen && !draftDock.isCollapsed ? <DraftInlineSlot /> : null,
    };

    return (
        <>
            {/* A bare flex axis with no chrome of its own — the same turn Tasks
                makes, for the same reason: the shell's <main> runs horizontally
                and this route stacks a bar over its content. It reuses md:gap-3
                so the seam above the console matches the seams inside it. */}
            <div className="flex min-h-0 min-w-0 flex-1 flex-col md:gap-3">
                {/* The bar is handed over rather than placed. Where it goes is
                    geometry, and geometry is the console's — it spans the whole
                    route normally and steps into the left column to give the
                    scratchpad its full height. */}
                <HomeConsole
                    panes={panes}
                    readOnly={readOnly}
                    // Two facts the scratchpad knows and the console reacts to:
                    // how many text columns the note is divided into (it earns a
                    // wider pane), and whether a second draft is open beside it
                    // (the two of them take the console outright).
                    draftSplit={!!draftDock?.isSplit}
                    draftColumns={draftDock?.columnCount ?? 1}
                    bar={(
                        <ProjectBar
                            project={project}
                            stats={stats}
                            links={project.links ?? NO_LINKS}
                            readOnly={readOnly}
                            draftCount={draftCount}
                            onNewTask={openTaskDialog}
                            onNewDoc={openDocDialog}
                            onNewDraft={openDraft}
                            onUpload={triggerUpload}
                            onManageLinks={manageLinks}
                        />
                    )}
                />
            </div>

            {!readOnly ? (
                <input id="quick-up" type="file" className="hidden" onChange={handleUpload} />
            ) : null}

            <Dialog open={taskDialogOpen} onOpenChange={setTaskDialogOpen}>
                <DialogContent className="rounded-2xl">
                    <DialogHeader><DialogTitle>New Task</DialogTitle></DialogHeader>
                    <form onSubmit={handleCreateTask} className="space-y-4 pt-2">
                        <Input
                            placeholder="What needs to be done?"
                            value={newTaskTitle}
                            onChange={(e) => setNewTaskTitle(e.target.value)}
                            className="rounded-xl"
                            autoFocus
                            required
                        />
                        <Button type="submit" className="w-full rounded-xl">Create Task</Button>
                    </form>
                </DialogContent>
            </Dialog>

            <Dialog open={docDialogOpen} onOpenChange={setDocDialogOpen}>
                <DialogContent className="rounded-2xl">
                    <DialogHeader><DialogTitle>New Document</DialogTitle></DialogHeader>
                    <Tabs defaultValue="create" className="w-full pt-2">
                        <TabsList className="grid w-full grid-cols-2 rounded-xl">
                            <TabsTrigger value="create" className="rounded-lg">Create</TabsTrigger>
                            <TabsTrigger value="upload" className="rounded-lg">Upload</TabsTrigger>
                        </TabsList>
                        <TabsContent value="create">
                            <form onSubmit={handleCreateDoc} className="space-y-4 pt-4">
                                <Input
                                    placeholder="document-name.md"
                                    value={newDocName}
                                    onChange={(e) => setNewDocName(e.target.value)}
                                    className="rounded-xl"
                                    autoFocus
                                    required
                                />
                                <Button type="submit" className="w-full rounded-xl">Create</Button>
                            </form>
                        </TabsContent>
                        <TabsContent value="upload" className="pt-4">
                            <Input type="file" onChange={handleUpload} className="rounded-xl" />
                        </TabsContent>
                    </Tabs>
                </DialogContent>
            </Dialog>

            <Dialog open={!!previewFile} onOpenChange={() => setPreviewFile(null)}>
                <DialogContent className="flex h-[80vh] max-h-[85vh] max-w-3xl flex-col rounded-2xl">
                    <DialogHeader className="flex-shrink-0">
                        <DialogTitle className="truncate font-mono">{previewFile?.name}</DialogTitle>
                    </DialogHeader>
                    <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-white/10 bg-black/50">
                        {previewFile?.type === 'asset' && previewFile.content?.startsWith('data:image') ? (
                            <div className="flex min-h-0 flex-1 items-center justify-center p-4">
                                <img
                                    src={previewFile.content}
                                    alt={previewFile.name}
                                    className="max-h-full max-w-full rounded-lg object-contain"
                                />
                            </div>
                        ) : isXmlFile(previewFile?.name) ? (
                            <XmlPreviewCompact content={previewFile?.content || ''} fileName={previewFile?.name} />
                        ) : (
                            <pre className="h-full min-h-0 w-full overflow-auto whitespace-pre-wrap p-4 text-left font-mono text-sm">
                                {previewFile?.content || 'No content.'}
                            </pre>
                        )}
                    </div>
                    <div className="flex flex-shrink-0 justify-end gap-2 pt-2">
                        <Button variant="outline" className="rounded-xl" onClick={() => setPreviewFile(null)}>
                            Close
                        </Button>
                        <Button variant="secondary" className="rounded-xl" onClick={() => handleDownload(previewFile)}>
                            <Download className="mr-2 h-4 w-4" /> Download
                        </Button>
                        <Button
                            className="rounded-xl"
                            onClick={() => {
                                const id = previewFile.id;
                                setPreviewFile(null);
                                navigate(`${baseUrl}/editor/${id}`);
                            }}
                        >
                            <ExternalLink className="mr-2 h-4 w-4" /> Open Editor
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
}
